import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Copyright from '../components/Copyright';
import NavBar from '../components/NavBar';
import { useEventList } from '../contexts/EventListProvider';
import { Event } from '../types';

const formatDate = (iso: string): string => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    });
};

const EventCard: React.FC<{ event: Event; linkToDetail?: boolean }> = ({ event, linkToDetail }) => {
    const [isHovered, setIsHovered] = useState(false);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    // Smooth spring configuration
    const springConfig = { damping: 20, stiffness: 150 };
    const springX = useSpring(mouseX, springConfig);
    const springY = useSpring(mouseY, springConfig);

    const handleMouseMove = (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left);
        mouseY.set(e.clientY - rect.top);
    };

    return (
        <Box
            data-testid="event-card"
            component={linkToDetail ? RouterLink : 'div'}
            to={linkToDetail ? `/events/${event.id}` : undefined}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onMouseMove={handleMouseMove}
            sx={{
                backgroundColor: '#0f1929',
                outline: '1px solid rgba(201,168,76,0.3)',
                outlineOffset: '4px',
                textDecoration: 'none',
                display: 'block',
                position: 'relative',
                '&:hover': linkToDetail ? { outline: '1px solid #c9a84c' } : {},
            }}
        >
            <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <img
                    src={event.image}
                    alt={linkToDetail ? `${event.title} - Click the image to view the detail page` : event.title}
                    style={{ width: '100%', display: 'block' }}
                />

                {linkToDetail && (
                    <>
                        {/* Desktop: Follow Mouse Badge */}
                        <motion.div
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                x: springX,
                                y: springY,
                                translateX: '-50%',
                                translateY: '-50%',
                                pointerEvents: 'none',
                                zIndex: 10,
                            }}
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{
                                opacity: isHovered ? 1 : 0,
                                scale: isHovered ? 1 : 0.5
                            }}
                            transition={{ duration: 0.2 }}
                            className="desktop-badge"
                        >
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    width: 100,
                                    height: 100,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(7,11,28,0.9)',
                                    border: '1px solid #c9a84c',
                                    backdropFilter: 'blur(4px)',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#c9a84c',
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textAlign: 'center',
                                        textTransform: 'uppercase',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    View<br />Recap
                                </Typography>
                            </Box>
                        </motion.div>

                        {/* Mobile: Static Persistent Badge */}
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                position: 'absolute',
                                bottom: 12,
                                right: 12,
                                backgroundColor: 'rgba(7,11,28,0.9)',
                                border: '1px solid #c9a84c',
                                borderRadius: '4px',
                                px: 1.5,
                                py: 0.5,
                                pointerEvents: 'none',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#c9a84c',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                View Recap
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
            <Box sx={{ p: 2.5 }}>
            <Box sx={{ width: 24, height: 2, backgroundColor: '#c9a84c', mb: 1.5 }} />
            <Typography
                data-testid="event-title"
                variant="h6"
                sx={{ fontFamily: '"Cormorant Garamond", serif', mb: 0.5, lineHeight: 1.3 }}
            >
                {event.title}
            </Typography>
            <Typography
                data-testid="event-date"
                variant="body2"
                sx={{ color: '#c9a84c', fontFamily: '"Inter", sans-serif', fontSize: '0.8rem', mb: 0.25 }}
            >
                {formatDate(event.date)}
            </Typography>
            {event.location && (
                <Typography
                    data-testid="event-location"
                    variant="body2"
                    sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif', fontSize: '0.8rem' }}
                >
                    {event.location}
                </Typography>
            )}
        </Box>
    </Box>
);
};

const SectionGrid: React.FC<{ events: Event[]; placeholder: string; isPast?: boolean }> = ({ events, placeholder, isPast }) => {
    if (events.length === 0) {
        return (
            <Typography
                data-testid="events-placeholder"
                variant="body2"
                sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif' }}
            >
                {placeholder}
            </Typography>
        );
    }
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 4,
            }}
        >
            {events.map(e => <EventCard key={e.id} event={e} linkToDetail={isPast} />)}
        </Box>
    );
};

const EventsPage: React.FC = () => {
    const { eventList, loading } = useEventList();
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = eventList.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
    const past = eventList.filter(e => e.date < today).sort((a, b) => b.date.localeCompare(a.date));

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <NavBar />
            <Box sx={{ pt: 14, pb: 8, px: { xs: 3, md: 8, lg: 12 } }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: 'primary.main' }} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 8 }}>
                            <Typography
                                data-testid="section-upcoming"
                                variant="h4"
                                sx={{ mb: 4, fontFamily: '"Cormorant Garamond", serif' }}
                            >
                                Upcoming
                            </Typography>
                            <SectionGrid events={upcoming} placeholder="No upcoming events at this time." />
                        </Box>

                        <Box>
                            <Typography
                                data-testid="section-past"
                                variant="h4"
                                sx={{ mb: 4, fontFamily: '"Cormorant Garamond", serif' }}
                            >
                                Past
                            </Typography>
                            <SectionGrid events={past} placeholder="No past events yet." isPast />
                        </Box>
                    </>
                )}
            </Box>
            <Copyright />
        </Box>
    );
};

export default EventsPage;
